import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Logo } from '@/components/Logo';
import type { LandingCopy, LandingLang } from './copy';
import { CloseIcon, MenuIcon } from './icons';

/**
 * Sticky header. From 900px: the section links, the language switch and Login in one row.
 * Narrower: the language switch stays, and the sections and Login move into a menu that
 * opens under the bar, closes on Escape or on a link, and is out of the tab order while shut.
 */
export const Header = ({
  copy,
  lang,
  onSwitchLang,
  linkProps,
}: {
  copy: LandingCopy;
  lang: LandingLang;
  onSwitchLang: () => void;
  linkProps: Record<string, () => void>;
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const sections: ReadonlyArray<[string, string]> = [
    ['#why', copy.nav.why],
    ['#how-it-works', copy.nav.how],
    ['#pricing', copy.nav.pricing],
    ['#faq', copy.nav.faq],
    ['#about', copy.nav.about],
  ];

  return (
    <header className="lp-header">
      <div className="lp-container lp-header__inner">
        <Link to="/" className="lp-brand">
          <Logo size={26} />
          <span>Order Bot</span>
        </Link>
        <nav className="lp-nav" aria-label={copy.nav.label}>
          {sections.map(([href, label]) => (
            <a key={href} href={href}>
              {label}
            </a>
          ))}
        </nav>
        <div className="lp-header__end">
          <button
            type="button"
            className="lp-lang"
            onClick={onSwitchLang}
            aria-label={copy.switchToLabel}
            lang={lang === 'hi' ? 'en' : 'hi'}
          >
            {copy.switchTo}
          </button>
          <Link
            to="/login"
            className="lp-btn lp-btn--small lp-btn--ghost lp-header__login"
            {...linkProps}
          >
            {copy.nav.login}
          </Link>
          <button
            type="button"
            className="lp-menu-btn"
            aria-expanded={menuOpen}
            aria-controls="lp-menu"
            aria-label={menuOpen ? copy.menu.close : copy.menu.open}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>
      <nav id="lp-menu" className="lp-menu" aria-label={copy.menu.label} hidden={!menuOpen}>
        <ul className="lp-container">
          {sections.map(([href, label]) => (
            <li key={href}>
              <a href={href} onClick={closeMenu}>
                {label}
              </a>
            </li>
          ))}
          <li>
            <Link to="/login" onClick={closeMenu} {...linkProps}>
              {copy.nav.login}
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
};
